import React, { useState, useEffect } from 'react';
import { partnershipAPI } from '../services/api';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AdminPartnershipRequests = () => {
  const { t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, reviewing, approved, rejected

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await partnershipAPI.getAll(params);
      setRequests(response.data.requests);
    } catch (error) {
      toast.error(t('toast.failedToLoadRequests'), { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await partnershipAPI.updateStatus(requestId, newStatus);
      toast.success(t('toast.statusUpdated'), { autoClose: 1000 });
      fetchRequests();
    } catch (error) {
      toast.error(t('toast.statusUpdateFailed'), { autoClose: 1000 });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      reviewing: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-display font-bold text-racing-black mb-2">
              {t('admin.partnershipRequests.title')}
            </h1>
            <p className="text-gray-600">
              {t('admin.partnershipRequests.subtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {['all', 'new', 'reviewing', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    filter === status
                      ? 'bg-racing-red text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t(`admin.partnershipRequests.filters.${status}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Requests List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-racing-red"></div>
              <p className="mt-4 text-gray-600">{t('admin.partnershipRequests.loading')}</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-600 text-lg">{t('admin.partnershipRequests.noRequests')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                >
                  {/* Request Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-racing-black">
                          {request.company_name}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            request.status
                          )}`}
                        >
                          {t(`admin.partnershipRequests.statuses.${request.status}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="font-semibold">{request.contact_person}</span>
                        <span>•</span>
                        <a
                          href={`mailto:${request.email}`}
                          className="text-racing-red hover:underline"
                        >
                          {request.email}
                        </a>
                        {request.phone && (
                          <>
                            <span>•</span>
                            <a
                              href={`tel:${request.phone}`}
                              className="hover:underline"
                            >
                              {request.phone}
                            </a>
                          </>
                        )}
                        {request.website && (
                          <>
                            <span>•</span>
                            <a
                              href={request.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {t('admin.partnershipRequests.fields.website')}
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </div>
                  </div>

                  {/* Brand Description */}
                  <div className="mb-4">
                    <h4 className="font-bold text-gray-900 mb-2">{t('admin.partnershipRequests.fields.brandDescription')}</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {request.brand_description}
                    </p>
                  </div>

                  {/* Cooperation Details */}
                  <div className="mb-4">
                    <h4 className="font-bold text-gray-900 mb-2">{t('admin.partnershipRequests.fields.cooperationDetails')}</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {request.cooperation_details}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <button
                      onClick={() => updateStatus(request.id, 'reviewing')}
                      disabled={request.status === 'reviewing'}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                      {t('admin.partnershipRequests.buttons.markAsReviewing')}
                    </button>
                    <button
                      onClick={() => updateStatus(request.id, 'approved')}
                      disabled={request.status === 'approved'}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                      {t('admin.partnershipRequests.buttons.approve')}
                    </button>
                    <button
                      onClick={() => updateStatus(request.id, 'rejected')}
                      disabled={request.status === 'rejected'}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                      {t('admin.partnershipRequests.buttons.reject')}
                    </button>
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent(`Re: Partnership Application - ${request.company_name}`);
                        const body = encodeURIComponent(`Hello ${request.contact_person},\n\n`);
                        window.open(`https://interia.pl/?compose&to=${request.email}&subject=${subject}&body=${body}`, '_blank');
                      }}
                      className="ml-auto px-4 py-2 bg-racing-red text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                    >
                      {t('admin.partnershipRequests.buttons.replyViaEmail')}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPartnershipRequests;
